import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PlacesAutocomplete, { Suggestion } from 'react-places-autocomplete';

import Fade from '@material-ui/core/Fade';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import { Check as CheckIcon, Search as SearchIcon } from '@material-ui/icons';
import clsx from 'clsx';

import { Address } from '../../types';
import { regExpSpecialSymbols } from '../../utils';
import { Input } from '../input/Input';
import { useStyles } from './AddressAutocomplete.styles';
import { AddressAutocompleteProps } from './AddressAutocomplete.types';
import { createAddress } from './AddressAutocomplete.utils';

/**
 * IMPORTANT:
 * To use this component, you are going to need to load Google Maps JavaScript API
 */
export const AddressAutocomplete = memo(
  ({
    inputValue: initialInputValue = '',
    withSearchIcon,
    checkFullness,
    searchOptions: searchOptionsProp,
    searchIcon,
    customClasses,
    onSelect,
    popperModifiers,
    popperDisablePortal = true,
    ...inputProps
  }: AddressAutocompleteProps) => {
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const styles = useStyles({ dropdownWidth: anchorRef.current?.offsetWidth });

    const [inputValue, setInputValue] = useState<string>(initialInputValue);
    const [isAddressSelected, setIsAddressSelected] = useState(!!inputValue);
    const [isAddressFull, setIsAddressFull] = useState(!!inputValue);

    const searchOptions = useMemo(
      () => ({
        componentRestrictions: { country: ['us', 'ca'] },
        types: ['address'],
        ...(searchOptionsProp ?? {}),
      }),
      [searchOptionsProp],
    );

    const getHighlightedText = useCallback(
      (text: Suggestion['formattedSuggestion']) => {
        const regExpValue = inputValue.replace(regExpSpecialSymbols, '\\$&');
        const parts = text.mainText.split(new RegExp(`(${regExpValue})`, 'gi'));
        const highlightIndex = parts.findIndex(part => part.toLowerCase() === inputValue.toLowerCase());

        return (
          <span>
            {parts.map((part, i) => (
              <span key={i} className={clsx(i === highlightIndex && styles.searchQueryMatch, styles.mainText)}>
                {part}
              </span>
            ))}
            {', '}
            {text.secondaryText}
          </span>
        );
      },
      [inputValue, styles],
    );

    const handleChange = useCallback(
      (value: string) => {
        setInputValue(value);

        if (inputProps.onInputChange) {
          inputProps.onInputChange(value);
        }

        if (isAddressSelected) {
          onSelect(null);
          setIsAddressSelected(false);
        }
      },
      [inputProps, isAddressSelected, onSelect],
    );

    const handleSelect = useCallback(
      async (value: string) => {
        setInputValue(value);

        const fullAddress = await createAddress(value);

        const isBroken = Object.keys(fullAddress).some(key => {
          const typpedKey = key as keyof Address;

          return key !== 'address2' && fullAddress[typpedKey] === '';
        });

        setIsAddressFull(!isBroken);

        onSelect({ inputValue: value, fullAddress });
        setIsAddressSelected(true);

        inputRef.current?.blur();
      },
      [onSelect],
    );

    useEffect(() => {
      setInputValue(initialInputValue);
    }, [initialInputValue]);

    return (
      <PlacesAutocomplete
        value={inputValue}
        searchOptions={searchOptions}
        onChange={handleChange}
        onSelect={handleSelect}
      >
        {({ suggestions, loading, getInputProps, getSuggestionItemProps }) => {
          const { onBlur: aCInputOnBlur, ...restAcInputProps } = getInputProps();
          const { ref: _ref, error, className, onBlur, ...restProps } = inputProps;

          const isOpenDropdown = !!suggestions.length;

          return (
            <>
              <div ref={anchorRef}>
                <Input
                  inputRef={inputRef}
                  variant="outlined"
                  size="medium"
                  error={error}
                  startAdornment={
                    withSearchIcon && (
                      <InputAdornment position="start" className={customClasses?.startAdornment}>
                        {searchIcon ?? <SearchIcon color="secondary" />}
                      </InputAdornment>
                    )
                  }
                  endAdornment={
                    isAddressSelected &&
                    !error &&
                    (!checkFullness || isAddressFull) && (
                      <InputAdornment position="end">
                        <CheckIcon className={styles.checkIcon} />
                      </InputAdornment>
                    )
                  }
                  className={clsx(className, styles.input, isOpenDropdown && 'noBottomRadius')}
                  onBlur={e => {
                    aCInputOnBlur(e);
                    onBlur?.(e);
                  }}
                  {...restAcInputProps}
                  {...restProps}
                  disabled={restAcInputProps.disabled || restProps.disabled}
                  inputProps={{
                    ...restProps.inputProps,
                    'aria-label': restProps.label || 'Address',
                    ...(error && { 'aria-invalid': true }),
                    'aria-autocomplete': 'list',
                  }}
                />
              </div>

              <Popper
                anchorEl={anchorRef.current}
                open={isOpenDropdown}
                placement="bottom"
                transition={true}
                disablePortal={popperDisablePortal}
                className={styles.dropdown}
                modifiers={popperModifiers}
              >
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps}>
                    <Paper elevation={4} className={styles.paper}>
                      {loading && (
                        <MenuItem className={clsx(styles.dropdownItem, styles.mainText)}>Loading...</MenuItem>
                      )}

                      {!loading &&
                        suggestions.map(suggestion => (
                          <MenuItem
                            {...getSuggestionItemProps(suggestion)}
                            key={suggestion.placeId}
                            className={clsx(styles.dropdownItem, suggestion.active && styles.activeDropdownItem)}
                          >
                            {getHighlightedText(suggestion.formattedSuggestion)}
                          </MenuItem>
                        ))}
                    </Paper>
                  </Fade>
                )}
              </Popper>
            </>
          );
        }}
      </PlacesAutocomplete>
    );
  },
);
